// Client helper for the Pages Functions API.
// VITE_API_BASE is normally empty (same origin); set it only when the SPA and the
// Workers API are served from different origins.

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

export async function createCheckout(
  cohortId: string,
  email: string,
): Promise<{ url: string }> {
  const res = await fetch(`${API_BASE}/api/checkout`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ cohort_id: cohortId, email }),
  });

  const data = (await res.json().catch(() => ({}))) as {
    url?: string;
    error?: string;
    cohort?: string;
  };

  if (!res.ok || !data.url) {
    const map: Record<string, string> = {
      sold_out: "This cohort is sold out.",
      cohort_not_found: "That cohort could not be found.",
      invalid_email: "Please enter a valid email address.",
      missing_cohort_id: "Please select a cohort.",
    };
    throw new Error(map[data.error ?? ""] ?? data.error ?? "Checkout failed. Please try again.");
  }

  return { url: data.url };
}

/**
 * Fetch the completion-certificate PDF from the Worker and open it in a new tab.
 * The Worker (functions/api/certificate.ts) returns application/pdf for a given
 * completion_record_id (or enrollment_id). Returns an error string on failure so
 * the UI can show a graceful message instead of a broken tab.
 */
export async function openCertificate(opts: {
  completionRecordId?: string;
  enrollmentId?: string;
}): Promise<{ error: string | null }> {
  const res = await fetch(`${API_BASE}/api/certificate`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      completion_record_id: opts.completionRecordId,
      enrollment_id: opts.enrollmentId,
    }),
  });

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    const map: Record<string, string> = {
      not_found: "Your completion record isn't ready yet.",
      not_configured: "Certificates aren't available yet (server not configured).",
      missing_id: "Could not identify your completion record.",
    };
    return {
      error: map[data.error ?? ""] ?? "Could not generate your certificate. Please try again.",
    };
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  // Revoke shortly after to let the new tab load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { error: null };
}
